import * as PropTypes from 'prop-types';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import DataConnector from 'connect/DataConnector';
import SchemaFieldSelectorManager from 'models/managers/SchemaFieldSelectorManager';
import {InventoryTableInsertionTableType} from 'notebook/integrations/samples/InventoryStructuredTableMode';
import {getSelectorIdsFromFields} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import SchemaFieldSelectorStore from 'registry/schema-field-selector/SchemaFieldSelectorStore';
import ContainerSchemaSelector from 'samples/ContainerSchemaSelector';
import * as SampleSchemaActions from 'samples/SampleSchemaActions';
import {ContainerSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaFieldSelector, SELECTOR_SHAPE} from 'schemas/SchemaFieldSelectorConstants';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';

interface ConnectedProps {
  selectorsById: {[selectorId: string]: SchemaFieldSelector};
}

class LegacyTransferTableInsertConnector extends DataConnector<
  TransferTableInsertModalProps,
  ConnectedProps
> {
  stores = [SchemaFieldSelectorStore];

  enhanceProps(): ConnectedProps {
    const selectorsById = this.schemaFieldSelectorStore().getAllSelectorsById({includeArchived: true});
    return {selectorsById};
  }
}

interface TransferTableInsertModalProps {
  onRequestSubmit: (
    insertionConfig: Exclude<LegacyTransferTableInsertionConfiguration, 'INVALID'>,
    selectorsById: {[selectorId: string]: SchemaFieldSelector}
  ) => void;
  onRequestClose: () => void;
}

export type LegacyTransferTableInsertionConfiguration =
  | {
      tableType: InventoryTableInsertionTableType.LEGACY_TRANSFER;
      containerSchema: ContainerSchema;
      parentStorableIsGrid: boolean;
    }
  | 'INVALID';

interface State {
  legacyTransferTableInsertionConfiguration: LegacyTransferTableInsertionConfiguration;
}

export class LegacyTransferTableInsertModal extends React.Component<
  TransferTableInsertModalProps & ConnectedProps,
  State
> {
  static propTypes = {
    onRequestSubmit: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    selectorsById: PropTypes.objectOf(SELECTOR_SHAPE.isRequired).isRequired,
  };

  state: State = {legacyTransferTableInsertionConfiguration: 'INVALID'};

  componentDidMount(): void {
    SampleSchemaActions.fetchAllContainerSchemas();
    SchemaFieldSelectorManager.fetchForAllRegistries();
  }

  isInsertEnabled(): boolean {
    if (this.state.legacyTransferTableInsertionConfiguration === 'INVALID') {
      return false;
    }
    const selectorIds = getSelectorIdsFromFields(
      this.state.legacyTransferTableInsertionConfiguration.containerSchema.fields
    );
    return !selectorIds.some((selectorId) => this.props.selectorsById[selectorId] == null);
  }

  handleInsert = (): void => {
    assert(
      this.state.legacyTransferTableInsertionConfiguration !== 'INVALID',
      'Expecting legacyTableInsertionConfig to be valid'
    );
    this.props.onRequestSubmit(
      this.state.legacyTransferTableInsertionConfiguration,
      this.props.selectorsById
    );
  };

  setLegacyTransferTableInsertionConfiguration = (
    newConfig: LegacyTransferTableInsertionConfiguration
  ): void => {
    this.setState({legacyTransferTableInsertionConfiguration: newConfig});
  };

  render(): JSX.Element {
    return (
      <Modal
        title="Insert transfer table"
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={this.handleInsert}
        buttons={[
          {
            text: 'Insert',
            submit: true,
            style: ButtonStyle.SECONDARY,
            disabled: !this.isInsertEnabled(),
            dataTestAttrs: {
              component: 'LegacyTransferTableInsertModal',
              element: 'button',
              key: ModalButtonKey.INSERT,
            },
          },
        ]}
        dataTestAttrs={{component: 'LegacyTransferTableInsertModal', element: 'root'}}
      >
        <LegacyTransferTableInsertForm onChange={this.setLegacyTransferTableInsertionConfiguration} />
      </Modal>
    );
  }
}

export function LegacyTransferTableInsertForm({
  onChange,
}: {
  onChange: (newInsertionConfig: LegacyTransferTableInsertionConfiguration) => void;
}): JSX.Element {
  const [containerSchema, setContainerSchema] = useState<ContainerSchema | null>(null);
  const [parentStorableIsGrid, setParentStorableIsGrid] = useState<boolean>(true);

  return (
    <>
      <FieldLabel label="Container type" />
      <ContainerSchemaSelector
        onChange={(newContainerSchema) => {
          setContainerSchema(newContainerSchema);
          if (newContainerSchema) {
            onChange({
              containerSchema: newContainerSchema,
              parentStorableIsGrid,
              tableType: InventoryTableInsertionTableType.LEGACY_TRANSFER,
            });
          }
        }}
        selectedContainerSchemaId={containerSchema && containerSchema.id}
        shouldShowNoSchemaOption={false}
      />
      <div className="u-small-padding-top">
        <label>
          <input
            checked={parentStorableIsGrid}
            onChange={(e) => {
              setParentStorableIsGrid(e.target.checked);
              if (containerSchema) {
                onChange({
                  containerSchema,
                  parentStorableIsGrid: e.target.checked,
                  tableType: InventoryTableInsertionTableType.LEGACY_TRANSFER,
                });
              }
            }}
            type="checkbox"
          />
          <span className="u-gray-darker">Containers will be inside a Plate/Box</span>
        </label>
      </div>
    </>
  );
}

export default LegacyTransferTableInsertConnector.connect(LegacyTransferTableInsertModal);
